'use client';

import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import HomeHeroSlider from '@/components/sections/Home/HomeHeroSlider';
import LazyViewportSection from '@/components/performance/LazyViewportSection';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import SalesCallbackProvider from '@/components/sales-callback/SalesCallbackProvider';
import SalesCallbackSection from '@/components/sales-callback/SalesCallbackSection';
import SectionEntryReveal from '@/components/util/SectionEntryReveal';
import { localizeUrl, stripHtml } from '@/lib/homepage-mappers';
import type { HomepageData, HomepageZone, ProjectsZone } from '@/types/homepage';
import type { ContactForm } from '@/types/contact-forms';

const HomeStrengthIntro = dynamic(
  () => import('@/components/sections/Home/HomeStrengthIntroWithImage'),
);
const HomeBuildStepsSection = dynamic(
  () => import('@/components/sections/Home/HomeBuildStepsSection'),
);
const HomeFullWidthProductSlider = dynamic(
  () => import('@/components/sections/Home/HomeFullWidthProductSlider'),
);
const CityMarvelsAccordionSection = dynamic(
  () => import('@/components/sections/Home/CityMarvelsAccordionSection'),
);
const HomeVoicesOfTrustShowcase = dynamic(
  () => import('@/components/sections/Home/HomeVoicesOfTrustShowcase'),
  {
    ssr: false,
  },
);
const HomePrecisionShowcase = dynamic(
  () => import('@/components/sections/Home/HomePrecisionShowcase'),
  { ssr: false },
);
const HomePreFooterSection = dynamic(
  () => import('@/components/sections/Home/HomePreFooterSection'),
  { ssr: false },
);

interface HomeProps {
  locale: string;
  homepage?: HomepageData | null;
  salesContactForm?: ContactForm | null;
  /**
   * Streaming slots. Above-fold sections (hero → testimonials) render
   * synchronously from `homepage` data. The two slots below stream from
   * separate Strapi fetches (`getBlogLanding`, `getLeadFormsWithFallback`)
   * via React Suspense, so the page commits + paints above the fold
   * without waiting for them.
   */
  blogCarouselSlot?: ReactNode;
  videoGallerySlot?: ReactNode;
  faqSlot?: ReactNode;
}

const PROJECT_INTRO_FALLBACK = {
  title: 'Marvels of Wonder',
  description:
    'Our commitment to quality doesn’t just build homes; it helps build the nation. We are proud to be the silent strength behind some of Bharat’s most iconic landmarks.',
  ctaText: 'View All Projects',
};

function renderProjectsZone(zone: ProjectsZone, locale: string) {
  const projectIntroTitle = zone.heading?.trim() || PROJECT_INTRO_FALLBACK.title;
  const projectIntroDescription = stripHtml(zone.subHeading) || PROJECT_INTRO_FALLBACK.description;
  const projectIntroCta = zone.button?.[0];
  const projectIntroCtaText = projectIntroCta?.buttonText || PROJECT_INTRO_FALLBACK.ctaText;
  const projectIntroCtaHref = localizeUrl(projectIntroCta?.buttonUrl, locale);

  const projectIntroButton = (
    <SwapTextButton
      variant="outline"
      text={projectIntroCtaText}
      className="w-full justify-center border-red hover:bg-red bg-transparent !px-5 !py-2.5 text-red sm:w-auto"
    />
  );

  return (
    <LazyViewportSection eager>
      <div className="relative z-0 isolate flex flex-col gap-8 md:gap-10">
        <SectionEntryReveal className="container mx-auto flex flex-col gap-5 px-4 text-center sm:px-6 md:gap-6 lg:flex-row lg:items-end lg:justify-between lg:px-0 lg:text-left">
          <div className="flex max-w-3xl flex-col text-black">
            <h2 className="text-3xl font-semibold leading-tight text-black sm:text-4xl md:text-5xl lg:text-6xl">
              {projectIntroTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xs leading-relaxed text-black/80 sm:text-sm lg:mx-0">
              {projectIntroDescription}
            </p>
          </div>
          <div className="w-full shrink-0 sm:w-auto lg:self-auto">
            {projectIntroCtaHref ? (
              <Link href={projectIntroCtaHref} {...getCmsButtonLinkProps(projectIntroCta)}>
                {projectIntroButton}
              </Link>
            ) : (
              projectIntroButton
            )}
          </div>
        </SectionEntryReveal>
        <CityMarvelsAccordionSection zone={zone} />
      </div>
    </LazyViewportSection>
  );
}

function renderHomepageZone(zone: HomepageZone, locale: string, faqSlot?: ReactNode) {
  switch (zone.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={zone.id} zone={zone} />;

    case 'homepage.parallax-with-stats':
      return (
        <LazyViewportSection key={zone.id} eager>
          <HomeStrengthIntro zone={zone} />
        </LazyViewportSection>
      );

    case 'homepage.step-by-step':
      return (
        <LazyViewportSection key={zone.id} eager>
          <HomeBuildStepsSection zone={zone} locale={locale} />
        </LazyViewportSection>
      );

    case 'homepage.product-usp':
      return (
        <SalesCallbackSection key={zone.id}>
          <LazyViewportSection eager>
            <HomeFullWidthProductSlider zone={zone} locale={locale} />
          </LazyViewportSection>
        </SalesCallbackSection>
      );

    case 'homepage.projects':
      return <div key={zone.id}>{renderProjectsZone(zone, locale)}</div>;

    case 'homepage.video-section':
      return (
        <LazyViewportSection key={zone.id} minHeight={840} rootMargin="200px 0px">
          <HomePrecisionShowcase zone={zone} locale={locale} />
        </LazyViewportSection>
      );

    case 'common.video-testimonial':
      return (
        <SalesCallbackSection key={zone.id}>
          <LazyViewportSection minHeight={760} rootMargin="200px 0px">
            <div className="relative z-10">
              <HomeVoicesOfTrustShowcase testimonialComponent={zone} />
            </div>
          </LazyViewportSection>
        </SalesCallbackSection>
      );

    case 'common.pre-footer':
      return (
        <LazyViewportSection key={zone.id} minHeight={190} rootMargin="1500px 0px">
          <HomePreFooterSection zone={zone} locale={locale} />
        </LazyViewportSection>
      );

    case 'modules.faqwith-form':
      return (
        <LazyViewportSection
          key={zone.id}
          minHeight={720}
          rootMargin="1500px 0px"
          fallbackClassName="bg-black"
        >
          {faqSlot}
        </LazyViewportSection>
      );

    default:
      return null;
  }
}

export default function Home({
  locale,
  homepage,
  salesContactForm,
  blogCarouselSlot,
  videoGallerySlot,
  faqSlot,
}: HomeProps) {
  const zones = homepage?.homepageZone ?? [];
  const banner = homepage?.bannerSlider ?? [];

  return (
    <SalesCallbackProvider formConfig={salesContactForm}>
      <main className="w-full">
        <section
          className={`${homepage && banner.length === 0 ? 'hidden' : ''} relative text-white`}
          suppressHydrationWarning
        >
          <HomeHeroSlider slides={banner} locale={locale} />
        </section>

        {zones.map((zone) => renderHomepageZone(zone, locale, faqSlot))}

        {/* Below-fold streaming slots. Each is rendered by an async Server
          Component wrapped in `<Suspense>` over in `page.tsx`, so the
          hero / above-fold paints first and these stream in once their
          Strapi calls resolve. */}
        {/* <LazyViewportSection minHeight={560} rootMargin="1500px 0px">
        {blogCarouselSlot}
      </LazyViewportSection> */}
      </main>
    </SalesCallbackProvider>
  );
}
